import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { getEndpoint } from '../../apiConstants';
import { API_ENDPOINTS } from '../../apiConstants';
import { 
  JobActivityLogApiResponse, 
  JobActivityLogData, 
  JobActivityLogSearchParams,
  JobActivityLogList 
} from './jobActivity.types';

// Helper function to format date
const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString();
};

// Helper function to calculate duration
const calculateDuration = (startTime: number, endTime: number): string => {
  const duration = endTime - startTime;
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((duration % (1000 * 60)) / 1000);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Transform API response to clean UI data
const transformJobActivityLog = (rawData: JobActivityLogApiResponse): JobActivityLogData => {
  return {
    id: rawData[0],
    jobName: rawData[1],
    lastConfiguredStartTime: rawData[2],
    lastConfiguredEndTime: rawData[3],
    lastUpdateBy: rawData[4],
    lastUpdatedDateTime: rawData[5],
    formattedLastConfiguredStart: formatDate(rawData[2]),
    formattedLastConfiguredEnd: formatDate(rawData[3]),
    formattedLastUpdated: formatDate(rawData[5]),
    duration: calculateDuration(rawData[2], rawData[3])
  };
};

// Build search parameters
const buildParams = (params: JobActivityLogSearchParams): Record<string, any> => {
  const searchParams: Record<string, any> = {
    searched: params.searched ?? true
  };

  if (params.firstResult !== undefined) searchParams.firstResult = params.firstResult;
  if (params.maxResult !== undefined) searchParams.maxResult = params.maxResult;
  if (params.clientId) searchParams.clientId = params.clientId;
  if (params.jobName) searchParams.jobName = params.jobName;
  if (params.lastUpdateBy) searchParams.lastUpdateBy = params.lastUpdateBy;

  return searchParams;
};

// Job Activity Log API
export const jobActivityLogApi = {
  fetch: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & JobActivityLogSearchParams): Promise<JobActivityLogData[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.JOB_ACTIVITY_LOG_LIST);
    const response: JobActivityLogApiResponse[] = await api.get(endpoint, buildParams(params));
    return response.map(transformJobActivityLog);
  },

  count: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & Omit<JobActivityLogSearchParams, 'firstResult' | 'maxResult'>): Promise<number> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.JOB_ACTIVITY_LOG_COUNT);
    const response = await api.get(endpoint, buildParams(params));
    
    logger.log('Job Activity Log Count Response:', response);
    
    // Handle both number and object responses
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      return response.count as number;
    } else {
      return 0;
    }
  },

  fetchWithCount: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & JobActivityLogSearchParams): Promise<JobActivityLogList> => {
    const [data, total] = await Promise.all([
      jobActivityLogApi.fetch({ clientCode, ...params }),
      jobActivityLogApi.count({ clientCode, ...params })
    ]);

    return { data, total };
  }
};

// Legacy exports for backward compatibility
export const fetchJobActivityLogs = jobActivityLogApi.fetch;
export const fetchJobActivityLogsCount = jobActivityLogApi.count;
